
import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlusCircle, Lock, LockOpen, Globe } from "lucide-react";
import { Solution } from "@/types/solutions";

interface SolutionGridViewProps {
  solutions: Solution[];
  addToCart: (solution: Solution) => void;
  cart: Solution[];
  getAgencyColor: (agency: string) => string;
  getCommercialTypeColor: (type: string) => string;
}

const SolutionGridView: React.FC<SolutionGridViewProps> = ({
  solutions,
  addToCart,
  cart,
  getAgencyColor,
  getCommercialTypeColor
}) => {
  const getContentTypeIcon = (contentType: string) => {
    switch (contentType) {
      case "Private":
        return <Lock className="h-4 w-4 text-gray-600" />;
      case "Semi-Private":
        return <LockOpen className="h-4 w-4 text-gray-600" />;
      case "Public":
        return <Globe className="h-4 w-4 text-gray-600" />;
      default:
        return null;
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {solutions.map((solution) => (
        <Card key={solution.id} className="overflow-hidden">
          <div className="p-4">
            <div className="flex justify-between items-start mb-3">
              <Badge variant="outline" className={getAgencyColor(solution.agency)}>
                {solution.agency}
              </Badge>
              <Badge variant="outline" className={getCommercialTypeColor(solution.commercialType)}>
                {solution.commercialType}
              </Badge>
            </div>
            <div className="flex items-center gap-2 mb-1">
              {getContentTypeIcon(solution.contentType)}
              <h3 className="font-medium">{solution.title}</h3>
            </div>
            <p className="text-xs text-gray-500 mb-1">{solution.category}</p>
            {solution.capturedDate && (
              <p className="text-xs text-gray-400 mb-2">Captured on {solution.capturedDate}</p>
            )}
            <p className="text-sm mb-4 text-gray-600 line-clamp-2">{solution.description}</p>
            <div className="flex justify-between items-center">
              <span className="font-semibold">${solution.price.toLocaleString()}</span>
              <Button 
                size="sm" 
                onClick={() => addToCart(solution)}
                disabled={cart.some(item => item.id === solution.id)}
              >
                {cart.some(item => item.id === solution.id) ? 
                  'Added' : 
                  <PlusCircle className="h-4 w-4" />
                }
              </Button>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Curator: {solution.curator === "Julia De Rosi" ? (
                <Link 
                  to="/julia-derosi-profile" 
                  className="text-blue-600 hover:text-blue-800 hover:underline ml-1"
                >
                  {solution.curator}
                </Link>
              ) : solution.curator === "Heather Edwards" ? (
                <Link 
                  to="/heather-edwards-profile" 
                  className="text-blue-600 hover:text-blue-800 hover:underline ml-1"
                >
                  {solution.curator}
                </Link>
              ) : solution.curator === "Charles Hardy" ? (
                <Link 
                  to="/charles-hardy-profile" 
                  className="text-blue-600 hover:text-blue-800 hover:underline ml-1"
                >
                  {solution.curator}
                </Link>
              ) : (
                solution.curator
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default SolutionGridView;
